import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import type { ContextChange, ContextChangePayload, ContextSource, ContextTrend } from './types';

const SOURCES: ContextSource[] = ['interno', 'externo'];
const TRENDS: ContextTrend[] = ['positivo', 'negativo', 'neutro'];

const SOURCE_LABEL: Record<ContextSource, string> = {
  interno: 'Interno',
  externo: 'Externo',
};

const TREND_LABEL: Record<ContextTrend, string> = {
  positivo: 'Positivo',
  negativo: 'Negativo',
  neutro: 'Neutro',
};

interface ContextChangeFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: ContextChange | null;
  onSubmit: (payload: ContextChangePayload) => Promise<void>;
}

export function ContextChangeFormModal({
  open,
  onOpenChange,
  initial,
  onSubmit,
}: ContextChangeFormModalProps) {
  const [source, setSource] = useState<ContextSource>('interno');
  const [situation, setSituation] = useState('');
  const [effect, setEffect] = useState('');
  const [guideline, setGuideline] = useState('');
  const [trend, setTrend] = useState<ContextTrend>('neutro');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSource(initial?.source ?? 'interno');
    setSituation(initial?.situation ?? '');
    setEffect(initial?.effect ?? '');
    setGuideline(initial?.guideline ?? '');
    setTrend(initial?.trend ?? 'neutro');
  }, [open, initial]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        source,
        situation: situation.trim(),
        effect: effect.trim(),
        guideline: guideline.trim() || null,
        trend,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle>
            {initial ? 'Editar cambio de contexto' : 'Nuevo cambio de contexto'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className='space-y-4'>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <label htmlFor='ctx-source' className='block text-sm font-medium mb-1'>
                Origen
              </label>
              <select
                id='ctx-source'
                value={source}
                onChange={(e) => setSource(e.target.value as ContextSource)}
                className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              >
                {SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {SOURCE_LABEL[s]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor='ctx-trend' className='block text-sm font-medium mb-1'>
                Tendencia
              </label>
              <select
                id='ctx-trend'
                value={trend}
                onChange={(e) => setTrend(e.target.value as ContextTrend)}
                className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              >
                {TRENDS.map((t) => (
                  <option key={t} value={t}>
                    {TREND_LABEL[t]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label htmlFor='ctx-situation' className='block text-sm font-medium mb-1'>
              Situación
            </label>
            <textarea
              id='ctx-situation'
              value={situation}
              onChange={(e) => setSituation(e.target.value)}
              required
              rows={3}
              placeholder='Describa la situación de contexto…'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
          </div>
          <div>
            <label htmlFor='ctx-effect' className='block text-sm font-medium mb-1'>
              Efecto
            </label>
            <textarea
              id='ctx-effect'
              value={effect}
              onChange={(e) => setEffect(e.target.value)}
              required
              rows={3}
              placeholder='Efecto sobre la organización…'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
          </div>
          <div>
            <label htmlFor='ctx-guideline' className='block text-sm font-medium mb-1'>
              Pauta / acción (opcional)
            </label>
            <textarea
              id='ctx-guideline'
              value={guideline}
              onChange={(e) => setGuideline(e.target.value)}
              rows={2}
              placeholder='Pauta de respuesta o seguimiento…'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
          </div>
          <div className='flex justify-end gap-2'>
            <button
              type='button'
              onClick={() => onOpenChange(false)}
              className='px-4 py-2 text-sm rounded-md border border-border hover:bg-muted'
            >
              Cancelar
            </button>
            <button
              type='submit'
              disabled={submitting || !situation.trim() || !effect.trim()}
              className='px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50'
            >
              {submitting ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
